<script setup lang="ts">
import DOMPurify from "dompurify"
import { storeToRefs } from "pinia"
import { ALButton, ALModal } from "@af-lib/vue-ui"
import { useRulesModalStore } from "@/components/af/RulesModal/rulesModal.store"
import SquareIcon from "@/components/af/Icons/SquareIcon.vue"

const rulesModalStore = useRulesModalStore()
const { closeRulesModal } = rulesModalStore
const { isRulesModalOpen, config, statementValue } =
  storeToRefs(rulesModalStore)

// Keyboard key icons injected in the {left_key} / {right_key} / {up_key} placeholders
const svgAttrs = `xmlns="http://www.w3.org/2000/svg" width="15" height="15" class="inline align-middle"`
const KEY_ICONS = {
  left_key: `<svg ${svgAttrs} viewBox="0 0 448 512"><path fill="currentColor" d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H109.3l105.3-105.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>`,
  right_key: `<svg ${svgAttrs} viewBox="0 0 448 512"><path fill="currentColor" d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224H32c-17.7 0-32 14.3-32 32s14.3 32 32 32h306.7L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"/></svg>`,
  up_key: `<svg ${svgAttrs} viewBox="0 0 15 15"><path fill="currentColor" d="M8.2 1.2c-.4-.4-1-.4-1.3 0L2.1 5.9c-.4.4-.4 1 0 1.3s1 .4 1.3 0l3.1-3.1v9c0 .5.4.9.9.9s.9-.4.9-.9v-9l3.1 3.1c.4.4 1 .4 1.3 0s.4-1 0-1.3L8.2 1.2z"/></svg>`,
}
</script>

<template>
  <ALModal
    v-if="config"
    v-model="isRulesModalOpen"
    borderStyle="ai"
    size="l"
    :title="`${$t(config.titleModal)} (${$t(config.subjectAssessment || '')})`"
    :isClosable="true"
  >
    <template #header-visual>
      <SquareIcon icon="fat fa-circle-info" />
    </template>
    <template #content>
      <div class="flex flex-col gap-3">
        <div class="border-af-grey-400 border-b pb-3">
          <h3 class="text-af-grey-700 mb-3 text-base font-bold">
            {{ $t(config.subtitleModal) }}
          </h3>
          <div v-if="config.descriptionAssessment" class="flex flex-col gap-2">
            <p
              v-for="description in config.descriptionAssessment"
              :key="description"
              class="text-af-grey-800 text-sm font-normal"
            >
              {{ $t(description) }}
            </p>
          </div>
        </div>
        <div>
          <h3 class="text-af-grey-700 mb-3 text-base font-bold">
            {{ $t(config.titleRules) }}
          </h3>
          <ul class="text-af-grey-800 flex list-disc flex-col gap-3 ps-5">
            <li
              v-for="rule in config.rules"
              :key="rule.id"
              class="text-sm font-normal"
            >
              <span
                v-if="rule.hasKeys"
                v-html="DOMPurify.sanitize($t(rule.textKey, KEY_ICONS))"
              />
              <template v-else>{{
                rule.needsStatement
                  ? $t(rule.textKey, { statement: statementValue })
                  : $t(rule.textKey)
              }}</template>
            </li>
          </ul>
        </div>
      </div>
    </template>
    <template #footer>
      <ALButton @click="closeRulesModal">
        {{ $t("general.tests.status.continue") }}
        <FaIcon icon="fas fa-arrow-right" />
      </ALButton>
    </template>
  </ALModal>
</template>
